import { createContext, useCallback, useContext, useEffect, useState } from "react";

const ThemeContext = createContext(null);
const MQ = "(prefers-color-scheme: dark)";
const systemTheme = () => (typeof window !== "undefined" && window.matchMedia && window.matchMedia(MQ).matches ? "dark" : "light");

/**
 * Theme provider.
 * - `preference` is what the user picked: "system" | "light" | "dark" (persisted as localStorage.theme).
 *   Users who never chose follow the phone/OS setting.
 * - `theme` is the resolved value (dark|light) actually applied.
 * - `forcedTheme` stack: dark-first public screens push via useForceDark; while non-empty dark wins,
 *   but the saved preference is untouched so it snaps back in-app.
 */
export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(() => {
    if (typeof window === "undefined") return "system";
    const v = localStorage.getItem("theme");
    return v === "light" || v === "dark" ? v : "system";
  });
  const [sys, setSys] = useState(systemTheme);
  const [forceDarkCount, setForceDarkCount] = useState(0);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(MQ);
    const onChange = () => setSys(mq.matches ? "dark" : "light");
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange); };
  }, []);

  const theme = preference === "system" ? sys : preference;

  useEffect(() => {
    const root = document.documentElement;
    const effective = forceDarkCount > 0 ? "dark" : theme;
    root.classList.toggle("dark", effective === "dark");
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", effective === "dark" ? "#0A0F1C" : "#FFFFFF");
    localStorage.setItem("theme", preference);
  }, [theme, preference, forceDarkCount]);

  // Header button: cycle System → Light → Dark → System
  const toggle = () => setPreference(p => (p === "system" ? "light" : p === "light" ? "dark" : "system"));
  const setTheme = setPreference;

  const pushForceDark = useCallback(() => setForceDarkCount(n => n + 1), []);
  const popForceDark  = useCallback(() => setForceDarkCount(n => Math.max(0, n - 1)), []);

  return (
    <ThemeContext.Provider value={{ theme, preference, systemTheme: sys, toggle, setTheme, pushForceDark, popForceDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
